import React, { useEffect, useMemo, useRef, useState } from 'react';
import JsBarcode from 'jsbarcode';
import { formatCurrency } from '../utils/currency';
import { effectiveSalePrice, pickRetailUom } from '../utils/uom';
import './ProductBarcodePrint.css';

/**
 * Gobbler / Xprinter XP-TT428B
 * Prints via local TSPL agent using BITMAP (name/price drawn as graphics).
 * Built-in TEXT fonts were not visible on this printer — only BARCODE showed.
 */
export const XP_TT428B_LABELS = {
  '100x50': {
    id: '100x50',
    name: '100 × 50 mm',
    widthMm: 100,
    heightMm: 50,
    columns: 1,
    gapMm: 0,
    moduleDots: 3,
    barcodeHeightDots: 140
  },
  '50x30': {
    id: '50x30',
    name: '50 × 30 mm',
    widthMm: 50,
    heightMm: 30,
    columns: 1,
    gapMm: 0,
    moduleDots: 2,
    barcodeHeightDots: 80
  },
  '50x25': {
    id: '50x25',
    name: '50 × 25 mm (1 across)',
    widthMm: 50,
    heightMm: 25,
    columns: 1,
    gapMm: 0,
    moduleDots: 2,
    barcodeHeightDots: 36
  },
  '50x25-2up': {
    id: '50x25-2up',
    name: '50 × 25 mm (2 across / one row)',
    widthMm: 50,
    heightMm: 25,
    columns: 2,
    gapMm: 0,
    moduleDots: 2,
    barcodeHeightDots: 36
  },
  '40x30': {
    id: '40x30',
    name: '40 × 30 mm',
    widthMm: 40,
    heightMm: 30,
    columns: 1,
    gapMm: 0,
    moduleDots: 2,
    barcodeHeightDots: 72
  }
};

const DOTS_PER_MM = 8;
const TSPL_AGENT_URL = 'http://127.0.0.1:9100';

function labelPageSize(label) {
  const cols = Math.max(1, Math.round(label.columns || 1));
  const gap = Number(label.gapMm) || 0;
  return {
    columns: cols,
    gapMm: gap,
    pageWidthMm: label.widthMm * cols + gap * Math.max(0, cols - 1),
    pageHeightMm: label.heightMm
  };
}

function chunkRows(items, size) {
  const chunks = [];
  for (let i = 0; i < items.length; i += size) {
    chunks.push(items.slice(i, i + size));
  }
  return chunks;
}

function formatPrintPrice(amount, unit) {
  const value = Number(amount);
  const price = Number.isFinite(value) ? `Rs ${value.toFixed(2)}` : 'Rs 0.00';
  const label = String(unit || '').trim();
  return label ? `${price} / ${label}` : price;
}

function extraSellUoms(product) {
  return (product?.uoms || []).filter((u) => {
    if (Number(u.is_base)) return false;
    const factor = parseFloat(u.conversion_to_base);
    return Number.isFinite(factor) && factor > 0;
  });
}

function uomOptionValue(uom, index) {
  if (uom?.id) return `uom:${uom.id}`;
  return `uom-code:${String(uom?.code || uom?.name || index)}`;
}

function findUomByOption(product, value) {
  const uoms = extraSellUoms(product);
  if (String(value || '').startsWith('uom:')) {
    const id = String(value).slice(4);
    return uoms.find((u) => String(u.id) === id) || null;
  }
  if (String(value || '').startsWith('uom-code:')) {
    const code = String(value).slice(9);
    return uoms.find((u, idx) => String(u.code || u.name || idx) === code) || null;
  }
  return null;
}

function resolveSticker(product, priceKind) {
  const uoms = product?.uoms || [];
  let uom = null;
  if (priceKind === 'retail') {
    uom = pickRetailUom(uoms);
  } else if (priceKind && priceKind !== 'product') {
    uom = findUomByOption(product, priceKind);
  }
  if (uom) {
    return {
      product,
      name: product?.name || '',
      barcode: String(uom.barcode || product?.barcode || '').trim(),
      price: effectiveSalePrice(uom, product?.price),
      unit: uom.code || uom.name || ''
    };
  }
  return {
    product,
    name: product?.name || '',
    barcode: String(product?.barcode || '').trim(),
    price: parseFloat(product?.price) || 0,
    unit: ''
  };
}

function priceOptionsForProducts(products) {
  const list = products || [];
  if (list.length === 1) {
    const product = list[0];
    const uoms = product?.uoms || [];
    const base = uoms.find((u) => Number(u.is_base));
    const opts = [{
      value: 'product',
      label: `Product price${base?.code ? ` (${base.code})` : ''} — ${formatCurrency(product?.price || 0)}`
    }];
    extraSellUoms(product).forEach((uom, index) => {
      const price = effectiveSalePrice(uom, product?.price);
      const name = uom.code || uom.name || 'Pack';
      const kind = parseFloat(uom.conversion_to_base) < 1 ? 'Retail' : 'Pack';
      opts.push({
        value: uomOptionValue(uom, index),
        label: `${kind} price (${name}) — ${formatCurrency(price)}`
      });
    });
    return opts;
  }
  const opts = [{ value: 'product', label: 'Product price' }];
  if (list.some((p) => pickRetailUom(p.uoms))) {
    opts.push({ value: 'retail', label: 'Retail price' });
  }
  return opts;
}

function barcodeOptsForPreview(label) {
  const moduleDots = Math.max(2, Math.round(label.moduleDots || 2));
  const compact = label.heightMm <= 30;
  return {
    width: moduleDots,
    height: Math.round((label.barcodeHeightDots || 80) * (compact ? 0.35 : 0.55)),
    fontSize: compact ? 12 : Math.max(12, moduleDots * 4),
    margin: moduleDots * (compact ? 2 : 8),
    textMargin: 1
  };
}

function BarcodePng({ value, barcodeOpts }) {
  const canvasRef = useRef(null);
  const [dataUrl, setDataUrl] = useState('');
  const optsKey = JSON.stringify(barcodeOpts || {});

  useEffect(() => {
    if (!value || !canvasRef.current) return;
    try {
      JsBarcode(canvasRef.current, String(value), {
        format: 'CODE128',
        displayValue: true,
        font: 'monospace',
        fontOptions: 'bold',
        textAlign: 'center',
        lineColor: '#000000',
        background: '#ffffff',
        ...(barcodeOpts || {})
      });
      setDataUrl(canvasRef.current.toDataURL('image/png'));
    } catch (e) {
      setDataUrl('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, optsKey]);

  if (!value) return <div className="barcode-missing">No barcode</div>;
  return (
    <>
      <canvas ref={canvasRef} className="barcode-canvas-hidden" aria-hidden="true" />
      {dataUrl ? <img src={dataUrl} alt={String(value)} className="barcode-img" /> : (
        <div className="barcode-missing">Barcode error</div>
      )}
    </>
  );
}

function renderStickerRowCanvas(chunk, label) {
  const { columns, gapMm, pageWidthMm, pageHeightMm } = labelPageSize(label);
  const W = Math.round(pageWidthMm * DOTS_PER_MM);
  const H = Math.round(pageHeightMm * DOTS_PER_MM);
  const cellW = Math.round(label.widthMm * DOTS_PER_MM);
  const gapPx = Math.round(gapMm * DOTS_PER_MM);
  const compact = pageHeightMm <= 30;
  // Keep content clear of the cut edge: 5mm top, 2mm bottom.
  const marginTopPx = Math.round(5 * DOTS_PER_MM);
  const marginBottomPx = Math.round(2 * DOTS_PER_MM);

  const canvas = document.createElement('canvas');
  canvas.width = W;
  canvas.height = H;
  const ctx = canvas.getContext('2d');
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, W, H);
  ctx.fillStyle = '#000000';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'top';

  for (let col = 0; col < columns; col += 1) {
    const item = chunk[col];
    if (!item) continue;
    const sticker = item.sticker || resolveSticker(item.product, item.priceKind);
    const x0 = col * (cellW + gapPx);
    const cx = x0 + cellW / 2;
    const pad = 6;
    const maxTextW = cellW - pad * 2;
    const contentBottom = H - marginBottomPx;

    const nameSize = compact ? 24 : 32;
    const priceSize = compact ? 26 : 34;
    let y = marginTopPx;

    ctx.font = `bold ${nameSize}px Arial, Helvetica, sans-serif`;
    let drawName = String(sticker?.name || '')
      .replace(/₹/g, 'Rs ')
      .replace(/[^\x20-\x7E]/g, '')
      .trim()
      .slice(0, 22);
    while (drawName.length > 3 && ctx.measureText(drawName).width > maxTextW) {
      drawName = `${drawName.slice(0, -2)}.`;
    }
    ctx.fillText(drawName || ' ', cx, y);
    y += nameSize + 1;

    // Price: normal weight (explicitly not bold), keep size.
    let priceText = formatPrintPrice(sticker?.price || 0, sticker?.unit);
    ctx.font = `normal ${priceSize}px Arial, Helvetica, sans-serif`;
    while (priceText.length > 8 && ctx.measureText(priceText).width > maxTextW) {
      priceText = `${priceText.slice(0, -2)}.`;
    }
    ctx.fillText(priceText, cx, y);
    y += priceSize; // no extra gap before barcode

    const code = String(sticker?.barcode || '').trim();
    if (!code) continue;

    const bcCanvas = document.createElement('canvas');
    const moduleDots = Math.max(2, Math.round(label.moduleDots || 2));
    const availH = Math.max(16, contentBottom - y);
    const barH = Math.min(compact ? 48 : 120, Math.max(24, availH - 12));
    try {
      JsBarcode(bcCanvas, code, {
        format: 'CODE128',
        width: moduleDots,
        height: barH,
        displayValue: true,
        font: 'monospace',
        fontOptions: 'bold',
        fontSize: compact ? 12 : 16,
        textMargin: 1,
        margin: 0,
        marginLeft: moduleDots * 4,
        marginRight: moduleDots * 4,
        marginTop: 0,
        marginBottom: 0,
        lineColor: '#000000',
        background: '#ffffff'
      });
    } catch (e) {
      continue;
    }

    const availW = cellW - pad * 2;
    let imgW = bcCanvas.width;
    let imgH = bcCanvas.height;
    const scale = Math.min(availW / imgW, availH / imgH, 1);
    imgW = Math.max(1, Math.floor(imgW * scale));
    imgH = Math.max(1, Math.floor(imgH * scale));
    if (y + imgH > contentBottom) {
      const s = (contentBottom - y) / imgH;
      imgW = Math.max(1, Math.floor(imgW * s));
      imgH = Math.max(1, Math.floor(imgH * s));
    }
    // Place barcode immediately under price (no empty gap).
    const imgX = x0 + Math.floor((cellW - imgW) / 2);
    ctx.imageSmoothingEnabled = false;
    ctx.drawImage(bcCanvas, imgX, y, imgW, imgH);
  }

  return canvas;
}

function canvasToTsplMono(canvas) {
  const width = canvas.width;
  const height = canvas.height;
  const ctx = canvas.getContext('2d');
  const imageData = ctx.getImageData(0, 0, width, height);
  const widthBytes = Math.ceil(width / 8);
  // XP-TT428B treats bit 1 as white and bit 0 as black (inverted vs some TSPL docs).
  const data = new Uint8Array(widthBytes * height);
  data.fill(0xff);

  for (let y = 0; y < height; y += 1) {
    for (let x = 0; x < width; x += 1) {
      const i = (y * width + x) * 4;
      const lum = imageData.data[i] * 0.299 + imageData.data[i + 1] * 0.587 + imageData.data[i + 2] * 0.114;
      if (lum < 200) {
        data[y * widthBytes + (x >> 3)] &= ~(0x80 >> (x & 7));
      }
    }
  }
  return { widthBytes, height, data };
}

function u8ToBinaryString(u8) {
  let s = '';
  const chunk = 0x8000;
  for (let i = 0; i < u8.length; i += chunk) {
    s += String.fromCharCode.apply(null, u8.subarray(i, Math.min(i + chunk, u8.length)));
  }
  return s;
}

/** Binary TSPL with BITMAP so name/price are always visible. */
function buildTsplBitmapJob(stickers, label, copies) {
  const { columns, pageWidthMm, pageHeightMm } = labelPageSize(label);
  const rows = [];
  (stickers || []).forEach((sticker) => {
    if (!sticker?.barcode) return;
    for (let i = 0; i < Math.max(1, copies); i += 1) {
      rows.push({ sticker });
    }
  });
  if (!rows.length) return null;

  const chunks = chunkRows(rows, columns);
  let job =
    `SIZE ${pageWidthMm} mm,${pageHeightMm} mm\r\n`
    + 'GAP 2 mm,0\r\n'
    + 'DIRECTION 1\r\n'
    + 'REFERENCE 0,0\r\n'
    + 'OFFSET 0 mm\r\n'
    + 'SET TEAR ON\r\n'
    + 'DENSITY 15\r\n'
    + 'SPEED 2\r\n';

  chunks.forEach((chunk) => {
    const canvas = renderStickerRowCanvas(chunk, label);
    const mono = canvasToTsplMono(canvas);
    job += 'CLS\r\n';
    job += `BITMAP 0,0,${mono.widthBytes},${mono.height},0,`;
    job += u8ToBinaryString(mono.data);
    job += '\r\nPRINT 1,1\r\n';
  });

  return job;
}

function encodeTsplJobForAgent(binaryJob) {
  const bytes = new Uint8Array(binaryJob.length);
  for (let i = 0; i < binaryJob.length; i += 1) bytes[i] = binaryJob.charCodeAt(i) & 0xff;
  let binary = '';
  const chunk = 0x8000;
  for (let i = 0; i < bytes.length; i += chunk) {
    binary += String.fromCharCode.apply(null, bytes.subarray(i, Math.min(i + chunk, bytes.length)));
  }
  return `B64:${btoa(binary)}`;
}

async function tryTsplAgentPrint(payload) {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), 12000);
  try {
    const health = await fetch(`${TSPL_AGENT_URL}/health`, {
      method: 'GET',
      signal: controller.signal
    });
    if (!health.ok) return false;
    const res = await fetch(`${TSPL_AGENT_URL}/print`, {
      method: 'POST',
      headers: { 'Content-Type': 'text/plain; charset=utf-8' },
      body: payload,
      signal: controller.signal
    });
    return res.ok;
  } catch (e) {
    return false;
  } finally {
    clearTimeout(timer);
  }
}

async function pingAgent(timeoutMs = 2000) {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);
  try {
    const res = await fetch(`${TSPL_AGENT_URL}/health`, {
      method: 'GET',
      signal: controller.signal
    });
    return res.ok;
  } catch (e) {
    return false;
  } finally {
    clearTimeout(timer);
  }
}

function wakeLocalAgent() {
  // Custom protocol registered by one-time setup (does not navigate the page away).
  const iframe = document.createElement('iframe');
  iframe.style.display = 'none';
  iframe.src = 'xpsticker:start';
  document.body.appendChild(iframe);
  setTimeout(() => {
    try { iframe.remove(); } catch (e) { /* ignore */ }
  }, 4000);
}

async function waitForAgent(maxMs = 15000) {
  const started = Date.now();
  while (Date.now() - started < maxMs) {
    if (await pingAgent(1500)) return true;
    await new Promise((r) => setTimeout(r, 700));
  }
  return false;
}

async function printViaAgentWithWake(payload) {
  if (await tryTsplAgentPrint(payload)) return true;
  wakeLocalAgent();
  const up = await waitForAgent(16000);
  if (!up) return false;
  return tryTsplAgentPrint(payload);
}

const ProductBarcodePrint = ({ products, onClose }) => {
  const [labelSize, setLabelSize] = useState('50x25-2up');
  const [copies, setCopies] = useState(1);
  const [priceKind, setPriceKind] = useState('product');
  const [status, setStatus] = useState('');
  const [agentOk, setAgentOk] = useState(null);

  const label = XP_TT428B_LABELS[labelSize] || XP_TT428B_LABELS['100x50'];
  const copyCount = Math.max(1, Math.min(50, parseInt(copies, 10) || 1));
  const priceOptions = useMemo(() => priceOptionsForProducts(products), [products]);

  useEffect(() => {
    if (!priceOptions.some((opt) => opt.value === priceKind)) {
      setPriceKind('product');
    }
  }, [priceOptions, priceKind]);

  const stickers = useMemo(
    () => (products || []).map((product) => resolveSticker(product, priceKind)),
    [products, priceKind]
  );

  const printRows = useMemo(() => {
    const rows = [];
    stickers.forEach((sticker, index) => {
      for (let i = 0; i < copyCount; i += 1) {
        rows.push({
          key: `${sticker.product?.id || index}-${sticker.unit || 'product'}-${i}`,
          sticker
        });
      }
    });
    return rows;
  }, [stickers, copyCount]);

  const page = labelPageSize(label);
  const previewRows = useMemo(
    () => chunkRows(printRows, page.columns),
    [printRows, page.columns]
  );

  useEffect(() => {
    if (!products?.length) return undefined;
    document.body.classList.add('barcode-print-active');
    return () => document.body.classList.remove('barcode-print-active');
  }, [products]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${TSPL_AGENT_URL}/health`, { method: 'GET' });
        if (!cancelled) setAgentOk(res.ok);
      } catch (e) {
        if (!cancelled) setAgentOk(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  if (!products?.length) return null;

  const handlePrint = async () => {
    if (!printRows.length) return;
    setStatus('Printing…');

    const job = buildTsplBitmapJob(stickers, label, copyCount);
    if (!job) {
      setStatus('');
      alert('No product barcodes to print');
      return;
    }

    const payload = encodeTsplJobForAgent(job);
    const ok = await printViaAgentWithWake(payload);
    if (ok) {
      setAgentOk(true);
      setStatus('Printed — name, price and barcode');
      return;
    }

    setAgentOk(false);
    setStatus('Print helper not installed on this PC');
    alert(
      'Sticker print helper is not running on this PC.\n\n'
      + 'ONE-TIME install from this website:\n'
      + '1) Click "Download installer"\n'
      + '2) Unzip the file\n'
      + '3) Double-click SETUP-ONCE.bat\n'
      + '4) Click Print again\n\n'
      + 'After that, Windows starts the helper at login — no .bat every time.\n'
      + 'If already installed, click "Try start helper" then Print.'
    );
  };

  return (
    <div className="barcode-print-container">
      <div className="barcode-print-actions no-print">
        <div className="barcode-print-settings">
          <div className="barcode-setting">
            <label htmlFor="barcode-label-size">Sticker size</label>
            <select
              id="barcode-label-size"
              value={labelSize}
              onChange={(e) => setLabelSize(e.target.value)}
            >
              {Object.values(XP_TT428B_LABELS).map((item) => (
                <option key={item.id} value={item.id}>{item.name}</option>
              ))}
            </select>
          </div>
          <div className="barcode-setting">
            <label htmlFor="barcode-copies">Copies each</label>
            <input
              id="barcode-copies"
              type="number"
              min="1"
              max="50"
              value={copies}
              onChange={(e) => setCopies(e.target.value)}
            />
          </div>
          <div className="barcode-setting barcode-setting-price">
            <label htmlFor="barcode-price-kind">Price on barcode</label>
            <select
              id="barcode-price-kind"
              value={priceKind}
              onChange={(e) => setPriceKind(e.target.value)}
            >
              {priceOptions.map((opt) => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </div>
        </div>
        <div className="barcode-print-buttons">
          <button type="button" onClick={handlePrint} className="btn btn-primary">
            Print
          </button>
          <a
            className="btn btn-secondary"
            href="/sticker-print-setup.zip"
            download="inventory-sticker-print-setup.zip"
            title="One-time install for this PC"
          >
            Download installer
          </a>
          <button type="button" onClick={onClose} className="btn btn-secondary">
            Close
          </button>
        </div>
      </div>

      <div className={`barcode-printer-tip no-print${agentOk === false ? ' warn' : ''}`}>
        <p>
          {agentOk === true && (
            <>Printer ready — click <strong>Print</strong>.</>
          )}
          {agentOk === false && (
            <>
              Printer helper is off or not installed on this PC.
              {' '}
              <a href="/sticker-print-setup.zip" download="inventory-sticker-print-setup.zip">
                Download installer
              </a>
              {' '}
              → unzip → run <strong>SETUP-ONCE.bat</strong> once, then Print.
              {' '}
              <button
                type="button"
                className="btn btn-secondary btn-sm"
                style={{ marginLeft: 6 }}
                onClick={async () => {
                  wakeLocalAgent();
                  setStatus('Starting helper…');
                  const up = await waitForAgent(16000);
                  setAgentOk(up);
                  setStatus(up ? 'Helper ready — click Print' : 'Install still needed — download installer above');
                }}
              >
                Try start helper
              </button>
            </>
          )}
          {agentOk === null && <>Checking printer helper…</>}
        </p>
        <p>
          Size: <strong>{page.pageWidthMm} × {page.pageHeightMm} mm</strong>
          {page.columns > 1 ? ' (2 stickers / row)' : ''}.
          {status ? <> — <strong>{status}</strong></> : null}
        </p>
        <p>
          {priceKind === 'product'
            ? 'Printing the product price (box / bag).'
            : 'Printing the retail / pack price. Products without a retail pack keep the product price.'}
        </p>
      </div>

      <div className="barcode-print-content no-print">
        <div
          className={`barcode-labels-roll${page.columns > 1 ? ' multi-col' : ''}`}
          data-label-size={label.id}
          style={{
            '--label-w': `${label.widthMm}mm`,
            '--label-h': `${label.heightMm}mm`,
            '--page-w': `${page.pageWidthMm}mm`
          }}
        >
          {previewRows.map((row, rowIndex) => (
            <div key={`row-${rowIndex}`} className="barcode-label-row">
              {row.map(({ key, sticker }) => (
                <div
                  key={key}
                  className="barcode-label"
                  style={{ width: `${label.widthMm}mm`, height: `${label.heightMm}mm` }}
                >
                  <div className="barcode-label-name">{sticker.name}</div>
                  {sticker.product?.sku ? <div className="barcode-label-sku">{sticker.product.sku}</div> : null}
                  <div className="barcode-label-price">
                    {formatCurrency(sticker.price || 0)}
                    {sticker.unit ? ` / ${sticker.unit}` : ''}
                  </div>
                  <BarcodePng value={sticker.barcode} barcodeOpts={barcodeOptsForPreview(label)} />
                </div>
              ))}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default ProductBarcodePrint;
